import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Send, CheckCircle, ChevronDown, ChevronUp, Eye, Edit, FileText, DollarSign, Users } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

export const statusConfig = {
  draft: { color: "bg-slate-100 text-slate-700", icon: FileText, label: "Draft" },
  sent: { color: "bg-blue-100 text-blue-700", icon: Send, label: "Sent" },
  complete: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle, label: "Complete" }
};

export const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card className="bg-white border-slate-200 shadow-sm">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const PaymentCard = ({ payment, onStatusChange, onPreview, onEdit, onSend }) => {
  const [expanded, setExpanded] = useState(false);
  const status = statusConfig[payment.status] || statusConfig.draft;
  const StatusIcon = status.icon;

  return (
    <motion.div
      layout
      className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden"
    >
      <div
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${status.color} flex items-center justify-center flex-shrink-0`}>
            <StatusIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-medium text-slate-900">{payment.provider}</span>
              <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge variant="outline" className="text-xs border-slate-200">
                <Users className="w-3 h-3 mr-1" />
                {payment.client}
              </Badge>
              <Badge variant="outline" className="text-xs border-slate-200">
                <FileText className="w-3 h-3 mr-1" />
                {payment.provider_invoice_no}
              </Badge>
              {payment.provider_invoice_amount && (
                <Badge variant="outline" className="text-xs border-slate-200">
                  <DollarSign className="w-3 h-3 mr-1" />
                  {payment.provider_invoice_currency} {payment.provider_invoice_amount}
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-600 line-clamp-1">
              <span className="font-medium">Issue:</span> {payment.issue || "No issue specified"}
            </p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2">
            {payment.status === "draft" && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onSend(payment);
                }}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Send className="w-3 h-3 mr-1" />
                Send
              </Button>
            )}
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-4">
              {/* Email Preview */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Draft
                </h4>
                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600 whitespace-pre-wrap font-mono">
                  {payment.email_draft}
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Invoice Date:</span>
                  <p className="font-medium">{payment.provider_invoice_date || "N/A"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Provider Country:</span>
                  <p className="font-medium">{payment.provider_country || "N/A"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Client Country:</span>
                  <p className="font-medium">{payment.client_country || "N/A"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Provider ID:</span>
                  <p className="font-medium">{payment.provider_id || "N/A"}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview(payment);
                    }}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(payment);
                    }}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit Draft
                  </Button>
                </div>
                <div className="flex gap-2">
                  {["draft", "sent", "complete"].map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={payment.status === s ? "default" : "outline"}
                      onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange(payment.id, s);
                      }}
                      className={payment.status === s ? "bg-[#425B76]" : ""}
                    >
                      {statusConfig[s].label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
